/* Shared, progressively enhanced carousel dots. Existing cart and media sources stay untouched. */
(() => {
  if (window.sbCarouselDotsReady) return;
  window.sbCarouselDotsReady = true;
  const records = new Map();
  let nextId = 0;
  const words = {
    pt: ['Escolher imagem', 'Imagem', 'de'],
    en: ['Choose image', 'Image', 'of'],
    es: ['Elegir imagen', 'Imagen', 'de'],
    ru: ['Выбрать изображение', 'Изображение', 'из']
  };
  const language = (document.documentElement.lang || 'pt').toLowerCase().split('-')[0];
  const text = words[language] || words.pt;
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function dots(nav, target, move) {
    const group = document.createElement('div');
    group.className = 'sb-carousel-dots';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', text[0]);
    if (!target.id) target.id = `SbDotGallery-${++nextId}`;
    const legacy = [...nav.children].filter(el => !el.classList.contains('slideshow__autoplay'));
    legacy.forEach(el => el.classList.add('sb-dots-legacy'));
    nav.append(group);
    nav.classList.add('sb-dots-nav');
    nav.dataset.sbDotsReady = 'true';
    let buttons = [];
    let active = -1;
    group.addEventListener('click', event => {
      const button = event.target.closest('.sb-carousel-dot');
      if (!button || !group.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      move(Number(button.dataset.sbDotIndex));
    });
    // Dawn closes a media modal on mouse pointerup; dot controls must not close it.
    group.addEventListener('pointerup', event => event.stopPropagation());
    group.addEventListener('keydown', event => {
      const button = event.target.closest('.sb-carousel-dot');
      if (!button) return;
      let index = Number(button.dataset.sbDotIndex);
      if (event.key === 'ArrowRight') index = (index + 1) % buttons.length;
      else if (event.key === 'ArrowLeft') index = (index - 1 + buttons.length) % buttons.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = buttons.length - 1;
      else return;
      event.preventDefault();
      event.stopPropagation();
      move(index);
      buttons[index]?.focus({ preventScroll: true });
    });
    return {
      render(count, index, visible = true) {
        count = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
        const show = visible && count > 1;
        const state = String(show);
        if (nav.dataset.sbDotsVisible !== state) nav.dataset.sbDotsVisible = state;
        if (buttons.length !== count) {
          buttons = Array.from({ length: count }, (_, n) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'sb-carousel-dot';
            button.dataset.sbDotIndex = String(n);
            button.setAttribute('aria-label', `${text[1]} ${n + 1} ${text[2]} ${count}`);
            button.setAttribute('aria-controls', target.id);
            const mark = document.createElement('span');
            mark.setAttribute('aria-hidden', 'true');
            button.append(mark);
            return button;
          });
          group.replaceChildren(...buttons);
          active = -1;
        }
        const current = clamp(Math.round(index) || 0, 0, Math.max(0, count - 1));
        if (active === current) return;
        active = current;
        buttons.forEach((button, n) => {
          button.setAttribute('aria-current', String(n === current));
          button.tabIndex = n === current ? 0 : -1;
        });
        const dot = buttons[current];
        if (show && dot && group.clientWidth && group.scrollWidth > group.clientWidth + 1) {
          const left = dot.offsetLeft;
          if (left < group.scrollLeft || left + dot.offsetWidth > group.scrollLeft + group.clientWidth) {
            group.scrollTo({ left: Math.max(0, left - (group.clientWidth - dot.offsetWidth) / 2), behavior: 'auto' });
          }
        }
      },
      destroy() {
        group.remove();
        legacy.forEach(el => el.classList.remove('sb-dots-legacy'));
        nav.classList.remove('sb-dots-nav');
        delete nav.dataset.sbDotsReady;
        delete nav.dataset.sbDotsVisible;
      }
    };
  }

  function bindTrack(track, nav, host = null, createdNav = false) {
    if (!track || !nav || records.has(track)) return;
    const abort = new AbortController();
    const options = { signal: abort.signal };
    let frame = 0;
    let positions = [];
    const control = dots(nav, track, index => {
      if (positions[index] === undefined) return;
      if (host?.matches('slideshow-component') && host.sliderControlLinksArray?.[index]) {
        host.sliderControlLinksArray[index].click();
      } else {
        if (host?.matches('sweetbar-google-reviews')) {
          host.toggles?.forEach(button => {
            if (button.getAttribute('aria-expanded') === 'true') host.setExpanded(button, false);
          });
        }
        track.scrollTo({ left: positions[index], behavior: reduced() ? 'auto' : 'smooth' });
      }
    });
    function update() {
      if (!track.isConnected) return;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const items = [...track.children].filter(el => el instanceof HTMLElement && el.clientWidth > 0);
      positions = [];
      if (track.clientWidth && max > 2 && items.length > 1) {
        const first = items[0].getBoundingClientRect().left;
        items.forEach(el => {
          const position = clamp(el.getBoundingClientRect().left - first, 0, max);
          if (!positions.length || Math.abs(position - positions[positions.length - 1]) > 2) positions.push(position);
        });
        if (positions.length && max - positions[positions.length - 1] > 2) positions.push(max);
      }
      let active = 0;
      positions.forEach((position, index) => {
        if (Math.abs(track.scrollLeft - position) < Math.abs(track.scrollLeft - positions[active])) active = index;
      });
      control.render(positions.length, active, max > 2);
    }
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    track.addEventListener('scroll', schedule, { ...options, passive: true });
    host?.addEventListener('slideChanged', schedule, options);
    const resize = new ResizeObserver(schedule);
    resize.observe(track);
    const mutations = new MutationObserver(changes => {
      if (changes.some(change => change.target === track || change.target.parentElement === track)) schedule();
    });
    mutations.observe(track, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
    document.fonts?.ready.then(() => { if (track.isConnected) schedule(); });
    records.set(track, () => {
      abort.abort(); resize.disconnect(); mutations.disconnect(); cancelAnimationFrame(frame);
      control.destroy(); if (createdNav) nav.remove();
    });
    update();
  }

  function swipe(stage, step, signal) {
    let start = null;
    stage.dataset.sbDotsSwipe = 'true';
    stage.addEventListener('touchstart', event => {
      const target = event.target instanceof Element ? event.target : null;
      if (event.touches.length !== 1 || target?.closest('button,a,input,textarea')) { start = null; return; }
      const touch = event.touches[0];
      start = { x: touch.clientX, y: touch.clientY, time: performance.now() };
    }, { passive: true, signal });
    stage.addEventListener('touchend', event => {
      if (!start || event.touches.length || !event.changedTouches.length) { start = null; return; }
      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const elapsed = performance.now() - start.time;
      start = null;
      const scale = window.visualViewport?.scale || 1;
      if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.4 && elapsed < 1100 && scale <= 1.05) step(dx < 0 ? 1 : -1);
    }, { passive: true, signal });
    stage.addEventListener('touchcancel', () => { start = null; }, { passive: true, signal });
  }

  function bindPhoto(nav) {
    if (records.has(nav)) return;
    const postcard = nav.closest('sweetbar-postcard-picker');
    const modal = nav.closest('product-modal');
    const owner = postcard || modal;
    const dialog = postcard ? nav.closest('.pc-photo-dialog') : modal;
    const stage = postcard ? dialog?.querySelector('.pc-photo-stage') : modal?.querySelector('.product-media-modal__content');
    const counter = postcard ? nav.querySelector('[data-photo-count]') : modal?.photoCounter;
    if (!owner || !dialog || !stage || !counter) return;
    const abort = new AbortController();
    const move = index => {
      const count = owner.photoImages?.length || 0;
      if (!count) return;
      index = (index + count) % count;
      if (postcard) { owner.photoIndex = index; owner.showPhoto(); }
      else owner.stepPhoto(index - owner.photoIndex);
      update();
    };
    const control = dots(nav, stage, move);
    const update = () => control.render(owner.photoImages?.length || 0, owner.photoIndex || 0,
      postcard ? dialog.open : modal.hasAttribute('open') && modal.classList.contains('sb-photo-mode'));
    const watch = new MutationObserver(update);
    watch.observe(counter, { childList: true, characterData: true, subtree: true });
    watch.observe(dialog, { attributes: true, attributeFilter: ['open', 'class'] });
    swipe(stage, step => move((owner.photoIndex || 0) + step), abort.signal);
    if (postcard) {
      dialog.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault(); event.stopPropagation();
        move((owner.photoIndex || 0) + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal: abort.signal });
    } else {
      // Recompute focus endpoints because the original arrow controls are now hidden.
      modal.addEventListener('keydown', event => {
        if (event.key !== 'Tab' || !modal.hasAttribute('open') || !modal.classList.contains('sb-photo-mode')) return;
        const visible = [...modal.querySelectorAll('button:not(:disabled),[tabindex="0"]')]
          .filter(el => el.tabIndex >= 0 && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        const first = visible[0]; const last = visible[visible.length - 1];
        if (!first || !last) return;
        if (event.shiftKey && event.target === first) { event.preventDefault(); event.stopPropagation(); last.focus(); }
        else if (!event.shiftKey && event.target === last) { event.preventDefault(); event.stopPropagation(); first.focus(); }
      }, { capture: true, signal: abort.signal });
    }
    records.set(nav, () => { abort.abort(); watch.disconnect(); delete stage.dataset.sbDotsSwipe; control.destroy(); });
    update();
  }

  function scan(root = document) {
    if (!(root instanceof Element) && root !== document) return;
    const each = (selector, run) => {
      if (root instanceof Element && root.matches(selector)) run(root);
      root.querySelectorAll(selector).forEach(run);
    };
    each('slider-component,slideshow-component', host => {
      if (host.closest('.announcement-bar,.utility-bar') || host.matches('[id^="GalleryThumbnails"]')) return;
      const track = host.querySelector('[id^="Slider-"]');
      const nav = [...host.querySelectorAll('.slider-buttons')].find(el => el.closest('slider-component,slideshow-component') === host);
      if (track && nav) bindTrack(track, nav, host);
    });
    each('sweetbar-postcard-picker [data-gallery]', gallery => bindTrack(gallery.querySelector('[data-track]'), gallery.querySelector('.pc-gallery-nav')));
    each('sweetbar-google-reviews', host => bindTrack(host.querySelector('[data-sb-review-track]'), host.querySelector('[data-sb-review-nav]'), host));
    each('.sweetbar-categories__track', track => {
      if (records.has(track)) return;
      const nav = document.createElement('div');
      track.after(nav);
      bindTrack(track, nav, null, true);
    });
    each('.pc-photo-nav,.sb-photo-nav', bindPhoto);
  }

  function boot() {
    scan();
    const changes = new MutationObserver(list => {
      let removed = false;
      list.forEach(change => {
        const target = change.target instanceof Element ? change.target : change.target.parentElement;
        if (target?.closest('.sb-dots-nav,.sb-carousel-dots,.slider-counter')) return;
        if (change.removedNodes.length) removed = true;
        change.addedNodes.forEach(node => { if (node instanceof Element) scan(node); });
      });
      if (removed) records.forEach((dispose, node) => { if (!node.isConnected) { dispose(); records.delete(node); } });
    });
    changes.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', event => scan(event.target));
    window.addEventListener('pageshow', () => scan());
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
